import { motion } from 'framer-motion';
import { GraduationCap, Play, BookOpen, Home, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const courses = [
  {
    title: "Social Science Fundamentals",
    description: "Explore the foundations of sociology and social theory with Grad Fawkes.",
    playlistUrl: "https://www.youtube.com/playlist?list=PLn8UbQXnz_EwC1Iev4OTQrYme4XpY9_O7",
    thumbnail: "https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/bowuscbv_thumbnail-1773990327383.png"
  },
  {
    title: "Rhythm of Society",
    description: "A comprehensive exploration of policy, perception, and social control.",
    playlistUrl: "https://www.youtube.com/playlist?list=PLn8UbQXnz_EwC1Iev4OTQrYme4XpY9_O7",
    thumbnail: "https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/fm3gp32a_thumbnail-1773996233914.png"
  }
];

export default function CoursesPage() {
  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="courses-page">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
            <GraduationCap className="w-10 h-10 text-secondary" />
          </div>
          <h1 className="section-header mb-4">Courses & Education</h1>
          <p className="section-subheader max-w-2xl mx-auto">
            Learn sociology, social science, and critical thinking from Grad Fawkes. 
            Free educational content that flips the script on conventional wisdom.
          </p>
        </motion.div>

        {/* Featured Playlist */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display' }}>
            Social Science Course
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="video-wrapper glow-orange">
              <iframe
                src="https://www.youtube.com/embed/videoseries?list=PLn8UbQXnz_EwC1Iev4OTQrYme4XpY9_O7"
                title="Social Science Course Playlist"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </motion.div>

        {/* Course Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16"
        >
          {courses.map((course, index) => (
            <motion.div
              key={course.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <a href={course.playlistUrl} target="_blank" rel="noopener noreferrer">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full">
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={course.thumbnail}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-1" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-bold mb-2">{course.title}</h3>
                    <p className="text-muted-foreground text-sm">{course.description}</p>
                  </CardContent>
                </Card>
              </a>
            </motion.div>
          ))}
        </motion.div>

        {/* Publications Link */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Card className="bg-card border-secondary/30 max-w-2xl mx-auto">
            <CardContent className="p-8">
              <BookOpen className="w-12 h-12 text-secondary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Explore Publications
              </h3>
              <p className="text-muted-foreground mb-6">
                Dive deeper with written materials, research documents, and the Rhythm of Society chapters.
              </p>
              <Link to="/publications">
                <Button className="btn-primary">
                  <BookOpen className="w-4 h-4 mr-2" />
                  View Publications
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>

        {/* Navigation Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4"
        >
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <Link to="/donate">
            <Button className="btn-primary gap-2">
              <Heart className="w-4 h-4" />
              Support / Donate
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
